/* Whoami context — resolves the signed-in user's role flags
   (is_executive / is_platform_admin) once and shares them across the app
   shell, so nav-link visibility and route guards read a single source
   instead of each calling /whoami separately.

   Mounted INSIDE the authenticated shell (RequireAuth has already run), so a
   null token simply yields whoami = null. Fail-closed: a failed/absent lookup
   is treated as non-executive (deny), so a transient error hides exec-only
   surfaces rather than exposing them. Mock/walkthrough mode has no backend —
   it's treated as executive so the demo still shows every surface (matches the
   pre-guard behavior). */

import { createContext, useContext, useEffect, useState, type ReactNode } from 'react';
import { Navigate, Outlet } from 'react-router-dom';
import { useAuth } from './MsalBootstrap';
import { whoami, type Whoami } from '@/api/client';

interface WhoamiState {
  whoami: Whoami | null;
  /** True once the lookup has settled (success or failure). */
  ready: boolean;
  /** Convenience: mock mode OR a resolved is_executive. */
  isExec: boolean;
  /** Convenience: mock mode OR a resolved is_platform_admin. */
  isAdmin: boolean;
  /** Convenience: mock mode OR accounting-staff/exec — may open the Accounting hub. */
  isAccounting: boolean;
  /** Convenience: mock mode OR accounting-controller/exec — may edit suppression rules. */
  isController: boolean;
  /** Convenience: mock mode OR executive / payroll_401k role — may see the
   *  401(k) Reports. NOT accounting-staff membership (2026-10-07). */
  canPayroll401k: boolean;
}

const WhoamiCtx = createContext<WhoamiState>({ whoami: null, ready: false, isExec: false, isAdmin: false, isAccounting: false, isController: false, canPayroll401k: false });

export function WhoamiProvider({ children }: { children: ReactNode }) {
  const { getAccessToken, ready: authReady, mocked } = useAuth();
  const [state, setState] = useState<WhoamiState>({ whoami: null, ready: false, isExec: false, isAdmin: false, isAccounting: false, isController: false, canPayroll401k: false });

  useEffect(() => {
    let cancelled = false;
    if (mocked) {
      setState({ whoami: null, ready: true, isExec: true, isAdmin: true, isAccounting: true, isController: true, canPayroll401k: true });
      return;
    }
    if (!authReady) return;
    (async () => {
      // Retry transient failures before failing closed. This lookup resolves
      // ONCE per shell mount and the guards redirect privileged routes to
      // /home when it fails — so a single transient error (DTU pressure,
      // rate-limit, network blip) used to hide Portfolio/Accounting/Admin
      // for the WHOLE session, recoverable only by a full page reload.
      // Three attempts (0.8s / 2.4s backoff) ride out a blip; only then do
      // we settle fail-closed. `ready` stays false while retrying, so the
      // guards keep rendering nothing rather than prematurely redirecting.
      for (let attempt = 0; attempt < 3; attempt++) {
        try {
          const token = await getAccessToken();
          const me = await whoami(token);
          if (!cancelled) setState({
            whoami: me, ready: true,
            isExec: !!me?.is_executive, isAdmin: !!me?.is_platform_admin,
            isAccounting: !!me?.is_accounting_staff || !!me?.is_executive,
            isController: !!me?.is_accounting_controller || !!me?.is_executive,
            canPayroll401k: !!me?.can_payroll_401k || !!me?.is_executive,
          });
          return;
        } catch {
          if (cancelled) return;
          if (attempt < 2) await new Promise((r) => setTimeout(r, 800 * 3 ** attempt));
          if (cancelled) return;
        }
      }
      // All attempts failed — fail closed (deny privileged surfaces).
      if (!cancelled) setState({ whoami: null, ready: true, isExec: false, isAdmin: false, isAccounting: false, isController: false, canPayroll401k: false });
    })();
    return () => { cancelled = true; };
  }, [getAccessToken, authReady, mocked]);

  return <WhoamiCtx.Provider value={state}>{children}</WhoamiCtx.Provider>;
}

export function useWhoami(): WhoamiState {
  return useContext(WhoamiCtx);
}

/* Route guard for executive-only routes (e.g. /portfolio). While the lookup
   is still resolving, render nothing — the shell chrome is already up and the
   splash/scene-suspense covers the gap. Once resolved, non-executives are
   redirected to /home so they can't load the page shell by direct URL. */
export function RequireExec() {
  const { ready, isExec } = useWhoami();
  if (!ready) return null;
  if (!isExec) return <Navigate to="/home" replace />;
  return <Outlet />;
}

/* Route guard for platform-admin-only routes (e.g. /admin). Same shape as
   RequireExec — redirect non-admins to /home once the lookup resolves. */
export function RequireAdmin() {
  const { ready, isAdmin } = useWhoami();
  if (!ready) return null;
  if (!isAdmin) return <Navigate to="/home" replace />;
  return <Outlet />;
}

/* Route guard for the Accounting hub (/accounting) — accounting staff or
   executives. Same fail-closed shape as RequireExec; the server-side SQL gate
   on the endpoints is the real enforcement, this hides the shell by URL. */
export function RequireAccounting() {
  const { ready, isAccounting } = useWhoami();
  if (!ready) return null;
  if (!isAccounting) return <Navigate to="/home" replace />;
  return <Outlet />;
}
